<template>
  <Dialog v-model="visible" :title="`结算单详情 ${bill?.no || ''}`" width="900px">
    <div v-loading="loading">
      <el-alert
        v-if="bill?.bonusOverPool"
        class="mb-12px"
        type="warning"
        :closable="false"
        title="调额后应发合计已超过池金额（P0 允许，请运营确认）"
      />
      <el-descriptions :column="3" border class="mb-16px">
        <el-descriptions-item label="状态">
          {{ labelOf(SHAREHOLDER_SETTLE_STATUS, bill?.status) }}
        </el-descriptions-item>
        <el-descriptions-item label="池金额">￥{{ fenToYuan(bill?.poolAmount || 0) }}</el-descriptions-item>
        <el-descriptions-item label="应发合计">￥{{ fenToYuan(bill?.bonusAmount || 0) }}</el-descriptions-item>
        <el-descriptions-item label="订单数">{{ bill?.orderCount }}</el-descriptions-item>
        <el-descriptions-item label="股东数">{{ bill?.partnerCount }}</el-descriptions-item>
        <el-descriptions-item label="备注">{{ bill?.remark || '-' }}</el-descriptions-item>
      </el-descriptions>

      <el-divider content-position="left">股东明细</el-divider>
      <el-table :data="bill?.items || []" size="small" max-height="280">
        <el-table-column label="会员" min-width="120">
          <template #default="{ row }">
            {{ row.nickname || row.userId }}
          </template>
        </el-table-column>
        <el-table-column label="等级" prop="levelName" width="100" />
        <el-table-column label="系统额" width="100">
          <template #default="{ row }">￥{{ fenToYuan(row.systemAmount || 0) }}</template>
        </el-table-column>
        <el-table-column label="调后额" width="140">
          <template #default="{ row }">
            <template v-if="bill?.status === 0">
              <el-input-number
                v-model="adjustMap[row.userId]"
                :min="0"
                :precision="2"
                size="small"
                class="!w-110px"
              />
            </template>
            <span v-else>￥{{ fenToYuan(row.adjustAmount || 0) }}</span>
          </template>
        </el-table-column>
        <el-table-column v-if="bill?.status === 0" label="操作" width="90">
          <template #default="{ row }">
            <el-button
              v-hasPermi="['distribution:shareholder:settle:create']"
              link
              type="primary"
              @click="saveAdjust(row)"
            >
              保存
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-divider content-position="left">订单明细</el-divider>
      <el-table :data="bill?.orders || []" size="small" max-height="240">
        <el-table-column label="订单ID" prop="orderId" width="100" />
        <el-table-column label="折后价" width="100">
          <template #default="{ row }">￥{{ fenToYuan(row.goodsDiscounted || 0) }}</template>
        </el-table-column>
        <el-table-column label="贡献额" width="100">
          <template #default="{ row }">￥{{ fenToYuan(row.contributeAmount || 0) }}</template>
        </el-table-column>
        <el-table-column label="锁定" prop="lockStatus" width="80" />
      </el-table>
    </div>
    <template #footer>
      <el-button @click="visible = false">关 闭</el-button>
      <el-button
        v-if="bill?.status === 0"
        v-hasPermi="['distribution:shareholder:settle:confirm']"
        type="primary"
        @click="doConfirm"
      >
        确认
      </el-button>
      <el-button
        v-if="bill?.status === 0"
        v-hasPermi="['distribution:shareholder:settle:cancel']"
        type="danger"
        @click="doCancel"
      >
        作废
      </el-button>
      <el-button
        v-if="bill?.status === 1"
        v-hasPermi="['distribution:shareholder:settle:settle']"
        type="success"
        @click="doSettle"
      >
        结算入账
      </el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { fenToYuan, yuanToFen, fenToYuanNumber } from '@/utils'
import * as SettleApi from '@/api/mall/trade/shareholder/settle'
import { SHAREHOLDER_SETTLE_STATUS, labelOf } from '../constants'

defineOptions({ name: 'ShareholderSettleDetailDialog' })
const emit = defineEmits(['success'])
const message = useMessage()
const visible = ref(false)
const loading = ref(false)
const bill = ref<SettleApi.ShareholderSettleBillVO>()
const adjustMap = reactive<Record<number, number>>({})

const load = async (id: number) => {
  loading.value = true
  try {
    bill.value = await SettleApi.getShareholderSettle(id)
    Object.keys(adjustMap).forEach((k) => delete adjustMap[Number(k)])
    ;(bill.value?.items || []).forEach((it) => {
      if (it.userId != null) {
        adjustMap[it.userId] = fenToYuanNumber(it.adjustAmount ?? it.systemAmount ?? 0)
      }
    })
  } finally {
    loading.value = false
  }
}

const open = async (id: number) => {
  visible.value = true
  await load(id)
}

const saveAdjust = async (row: NonNullable<SettleApi.ShareholderSettleBillVO['items']>[0]) => {
  const yuan = adjustMap[row.userId!]
  await SettleApi.adjustShareholderSettle({
    billId: bill.value!.id!,
    userId: row.userId!,
    adjustAmount: yuanToFen(yuan ?? 0)
  })
  message.success('调额已保存')
  await load(bill.value!.id!)
  emit('success')
}

const doConfirm = async () => {
  await message.confirm('确认后不可再调额，是否继续？')
  await SettleApi.confirmShareholderSettle(bill.value!.id!)
  message.success('已确认')
  visible.value = false
  emit('success')
}

const doCancel = async () => {
  await message.confirm('作废将解锁订单，是否继续？')
  await SettleApi.cancelShareholderSettle(bill.value!.id!)
  message.success('已作废')
  visible.value = false
  emit('success')
}

const doSettle = async () => {
  await message.confirm('确认执行结算入账？')
  await SettleApi.settleShareholderSettle(bill.value!.id!)
  message.success('已入账')
  visible.value = false
  emit('success')
}

defineExpose({ open })
</script>
